import React, { useState } from "react";
import * as m from "../../lib/model";
import { qsbsBump } from "./Qsbs";
import { Card, Tile, MoneyInput } from "../shared";
import { Timeline } from "./Timeline";
import { BuyerCards } from "./RoomTabs";
import { shareApi } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { classFor, sortClasses } from "../../lib/classes";
import { ClassBadge, ClassCard } from "./ClassCard";

const termsFor = (M, classes, amount) => { const c = classFor(classes, amount); return c ? { ...M, prefReturn: c.pref } : M; };
const fmtC = (n) => m.fmt$(n || 0);


export function CommitBar({ ask, committed, count, testid = "commit-bar" }) {
  const pctv = ask > 0 ? Math.min(100, (committed / ask) * 100) : 0;
  return (
    <div className="cbar" data-testid={testid}>
      <div className="cbar-head">
        <span>Soft equity committed</span>
        <span className="mono"><b>{m.fmt$(committed)}</b> of {m.fmt$(ask)} · {pctv.toFixed(0)}%{count ? ` · ${count} commitment${count === 1 ? "" : "s"}` : ""}</span>
      </div>
      <div className="cbar-track"><div className="cbar-fill" style={{ width: `${pctv}%` }} /></div>
    </div>
  );
}

function Calculator({ M, ask, minCommit, classes }) {
  const [amt, setAmt] = useState(() => Math.min(Math.max(ask, minCommit), Math.max(minCommit, Math.round(ask / 4 / 5000) * 5000) || minCommit));
  const cls = classFor(classes, amt);
  const TM = termsFor(M, classes, amt);
  const r = m.investorReturn(TM, amt || 0);
  const step = ask >= 2000000 ? 25000 : ask >= 500000 ? 5000 : 1000;
  const clamp = (v) => Math.max(minCommit, Math.min(Math.max(ask, minCommit), v));
  let cum = -amt;
  const rows = r.rows.map((x) => { cum += x.cash; return { ...x, cum }; });
  return (
    <Card title="Hypothetical Investor Calculator" className="table-scroll" testid="ir-calc">
      <div className="calc-bar">
        <label>Your investment</label>
        <div className="calc-bar-in">
          <MoneyInput value={amt} onChange={(v) => setAmt(+v || 0)} onBlur={() => setAmt((v) => clamp(v))} testid="ir-calc-input" />
          <span className="calc-share mono">{r.share.toFixed(1)}% of the {m.fmt$(ask)} raise</span>
          <ClassBadge cls={cls} testid="ir-calc-class" />
        </div>
        <div className="calc-presets">{[0.05, 0.1, 0.25, 0.5, 1].map((p) => <button key={p} type="button" className={Math.abs(amt - Math.round(ask * p)) < 1 ? "active" : ""} onClick={() => setAmt(clamp(Math.round(ask * p)))} data-testid={`ir-calc-preset-${p * 100}`}>{p * 100}%</button>)}</div>
        <input type="range" min={minCommit} max={Math.max(ask, minCommit)} step={step} value={Math.max(minCommit, amt)} onChange={(e) => setAmt(clamp(+e.target.value))} data-testid="ir-calc-slider" />
        {amt < minCommit && <div className="cls-min-warn" data-testid="ir-calc-min-warn">Minimum investment is {m.fmt$(minCommit)}.</div>}
      </div>
      <div className="grid g4" style={{ margin: "12px 0" }}>
        <Tile label="Total distributions" value={m.fmt$(r.total)} tone="go" testid="ir-calc-total" />
        <Tile label="Net profit" value={m.fmt$(r.profit)} tone={r.profit >= 0 ? "go" : "pass"} />
        <Tile label="MOIC" value={m.fmtX(r.moic)} tone={r.moic >= (M.moicTarget || 2) ? "go" : "amber"} testid="ir-calc-moic" />
        <Tile label="IRR" value={isNaN(r.irr) ? "n/a" : m.fmtPct(r.irr)} tone={r.irr >= 15 ? "go" : r.irr >= 8 ? "amber" : "pass"} testid="ir-calc-irr" />
      </div>
      <div className="calc-tbl-wrap"><table className="tbl calc-tbl" data-testid="ir-calc-table">
        <thead><tr><th>Investor cash flow</th><th className="num">Yr 0</th>{rows.map((x) => <th className="num" key={x.y}>Yr {x.y}</th>)}<th className="num">Total</th></tr></thead>
        <tbody>
          <tr><td>Cash flow{r.called > 0 ? <span className="om-sub"> · net of {fmtC(r.called)} add-on capital calls</span> : null}</td><td className="num exp">{fmtC(-amt)}</td>{rows.map((x) => <td className={`num ${x.cash >= 0 ? "pos" : "exp"}`} key={x.y}>{fmtC(x.cash)}</td>)}<td className="num pos"><b>{fmtC(r.profit)}</b></td></tr>
          <tr><td>Cash-on-cash yield</td><td className="num">—</td>{rows.map((x) => <td className="num" key={x.y}>{amt > 0 ? `${((x.cash / amt) * 100).toFixed(1)}%` : "—"}</td>)}<td className="num">{r.invested > 0 ? `${((r.total / r.invested) * 100).toFixed(0)}%` : "—"}</td></tr>
          <tr className="hl"><td><b>Cumulative position</b></td><td className="num exp">{fmtC(-amt)}</td>{rows.map((x) => <td className={`num ${x.cum >= 0 ? "pos" : "exp"}`} key={x.y}><b>{fmtC(x.cum)}</b></td>)}<td className={`num ${r.profit >= 0 ? "pos" : "exp"}`}><b>{fmtC(r.profit)}</b></td></tr>
        </tbody>
      </table></div>
      <div className="ir-fine">Modeled at the {cls ? `Class ${cls.name}` : "LP-class"} return profile — {TM.prefReturn}% preferred return, {TM.lpSplit}/{100 - TM.lpSplit} split after pref, base case, {TM.holdIndefinite ? "indefinite hold (cash-only view)" : `${TM.hold}-year hold`}. Illustrative only; not a guarantee of returns.</div>
    </Card>
  );
}

function CommitForm({ shareId, sessionId, investor, minCommit, classes, onDone }) {
  const [amount, setAmount] = useState(minCommit);
  const [name, setName] = useState(investor.name || "");
  const [email, setEmail] = useState(investor.email || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const cls = classFor(classes, amount);
  const below = amount < minCommit;
  const submit = async () => {
    if (!name.trim()) return toast.error("Please enter your name");
    if (!(amount > 0)) return toast.error("Enter a commitment amount");
    if (below) return toast.error(`Minimum commitment is ${m.fmt$(minCommit)}`);
    setBusy(true);
    try {
      const r = await shareApi.commit(shareId, { sessionId, name, email, amount, note });
      setDone(r.commit); onDone(r.commits);
      toast.success("Soft commitment recorded — the sponsor has been notified");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record commitment"); }
    finally { setBusy(false); }
  };
  if (done) return (
    <Card title="Your Soft Commitment" testid="ir-commit-done">
      <div className="commit-done"><CheckCircle2 size={22} /><div><b>{m.fmt$(done.amount)}</b> soft-committed by {done.name}{done.className ? <> as <b>Class {done.className}</b> ({done.pref}% preferred return)</> : null}.<br /><span style={{ color: "var(--text2)" }}>Non-binding indication of interest. The sponsor will follow up with subscription documents ahead of the capital call.</span></div></div>
    </Card>
  );
  return (
    <Card title="Indicate Your Soft Commitment" testid="ir-commit-form">
      <div className="field"><label>Commitment amount ($)</label>
        <div className="calc-bar-in" style={{ gap: 10 }}>
          <MoneyInput value={amount} onChange={(v) => setAmount(+v || 0)} testid="ir-commit-amount" style={{ flex: 1 }} />
          <ClassBadge cls={cls} testid="ir-commit-class" />
        </div>
        {below && <div className="cls-min-warn" data-testid="ir-commit-min-warn">Minimum commitment is {m.fmt$(minCommit)} — you can't soft-commit less than that.</div>}
      </div>
      <div className="grid g2">
        <div className="field"><label>Your name</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} data-testid="ir-commit-name" /></div>
        <div className="field"><label>Email</label><input className="inp" value={email} onChange={(e) => setEmail(e.target.value)} readOnly={!!investor.email} title={investor.email ? "From your deal room sign-in" : ""} data-testid="ir-commit-email" /></div>
      </div>
      <div className="field"><label>Note to the sponsor (optional)</label><textarea className="inp" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ir-commit-note" /></div>
      <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy || below || !(amount > 0)} onClick={submit} data-testid="ir-commit-submit">{busy ? "Recording…" : `Soft-commit ${m.fmt$(amount || 0)}${cls ? ` · Class ${cls.name}` : ""}`}</button>
      <div className="ir-fine">Minimum {m.fmt$(minCommit)}. Non-binding; subject to subscription documents and the capital call.</div>
    </Card>
  );
}

export function InvestTab({ M, shareId, sessionId, investor, room, onCommits, market }) {
  const buyers = Array.isArray(market?.consolidators) ? market.consolidators.slice(0, 6) : [];
  const wf = m.lpWaterfall(M);
  const bump = qsbsBump(M);
  const ask = room.equityAsk > 0 ? room.equityAsk : m.lpAskOf(M);
  const minCommit = room.minCommit || 25000;
  const classes = sortClasses(room.classes);
  const prefLabel = classes.length ? `${classes[classes.length - 1].pref}–${classes[0].pref}%` : `${M.prefReturn}%`;
  const c = room.commits || { total: 0, count: 0 };
  return (
    <>
      <div className="grid g4">
        <Tile label="The Ask — LP Equity" value={m.fmt$(ask)} sub="Total equity requested" testid="ir-ask" />
        <Tile label="Preferred return" value={prefLabel} sub={classes.length ? "by investment class" : "cumulative, paid first"} testid="ir-pref-tile" />
        <Tile label="Modeled LP MOIC" value={m.fmtX(wf.lpMoic)} tone="go"
          sub={bump ? `QSBS +${m.fmtX(bump.x)} after tax` : undefined} testid="it-lpmoic" />
        <Tile label="Modeled LP IRR" value={isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)} tone="go"
          sub={bump && bump.bps > 0 ? `QSBS +${bump.bps} bps after tax` : undefined} testid="it-lpirr" />
      </div>
      <Card title="Raise Progress" className="ir-gap" testid="ir-raise">
        <CommitBar ask={ask} committed={c.total} count={c.count} testid="ir-commit-bar" />
        {classes.length > 0 && <ClassCard classes={classes} />}
      </Card>
      <div className="ir-gap"><Calculator M={M} ask={ask} minCommit={minCommit} classes={classes} /></div>
      <div className="grid g2 ir-gap">
        <CommitForm shareId={shareId} sessionId={sessionId} investor={investor} minCommit={minCommit} classes={classes} onDone={onCommits} />
        <Card title="Deal Timeline" testid="ir-timeline-card"><Timeline items={room.timeline} testid="ir-timeline" /></Card>
      </div>
      {buyers.length > 0 && <Card className="ir-gap" title="Exit Path"><BuyerCards cons={buyers} title="Likely exit buyers for this platform" /></Card>}
    </>
  );
}
